import { Suspense, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Login } from './pages/guest/Login'
import { Register } from './pages/guest/Register'
import { ResetPassword } from './pages/guest/ResetPassword'
import { ForgotPassword } from './pages/guest/ForgotPassword'
import { Home } from './pages/guest/home/Home'
import { StaticHome } from './pages/guest/staticHome/StaticHome'
import Terms from './pages/guest/terms/Terms'
import { NotFound } from './pages/guest/notFound/NotFound'
import Dashboard from './pages/authenticated/Dashboard'
import History from './pages/authenticated/History'
import Reschedule from './pages/authenticated/Reschedule'
import Passes from './pages/authenticated/Passes'
import Confirm from './pages/authenticated/confirm/Confirm'
import Booking from './pages/authenticated/Booking'
import GuestBooking from './pages/guest/guestBooking/GuestBooking'
import GuestCheckout from './pages/guest/guestBooking/guestCheckout/GuestCheckout'
import GuestMemberships from './pages/guest/guestMemberships/GuestMemberships'
import GuestClubs from './pages/guest/guestClubs/GuestClubs'
import Account from './pages/authenticated/Account'
import AccountSetup from './pages/authenticated/accountSetup/AccountSetup'
import Partners from './pages/authenticated/Partners'
import Help from './pages/authenticated/help/Help'
import ManageAllBookings from './pages/authenticated/management/ManagerAllBookings'
import { Courts } from './pages/authenticated/management/ManagerCourts'
import { Court } from './pages/authenticated/management/ManagerCourt'
import ManagerBooking from './pages/authenticated/management/ManagerBooking'
import { ManagerClubs } from './pages/authenticated/management/ManagerClubs'
import { ManagerClub } from './pages/authenticated/management/ManagerClub'
import { ManagerPasses } from './pages/authenticated/management/ManagerPasses'
import { ManagerPass } from './pages/authenticated/management/ManagerPass'
import { ManagerPricing } from './pages/authenticated/management/ManagerPricing'
import { ManagerPriceRule } from './pages/authenticated/management/ManagerPriceRule'
import { ManagerBlocks } from './pages/authenticated/management/ManagerBlocks'
import { ManagerBlock } from './pages/authenticated/management/ManagerBlock'
import { AdminReports } from './pages/authenticated/management/AdminReports'
import { AdminUsers } from './pages/authenticated/management/AdminUsers'
import { AdminUser } from './pages/authenticated/management/AdminUser'
import { AdminAudit } from './pages/authenticated/management/AdminAudit'
import Payments from './pages/authenticated/management/Payments'
import Checkout from './pages/checkout/Checkout'
import CheckoutLayout from './components/checkoutLayout/CheckoutLayout'
import Loading from './components/loading/Loading'
import { GuestLayout } from './components/guest/layout/GuestLayout'
import ProfileCompletionGuard from './components/authenticated/guards/ProfileCompletionGuard'
import RoleGuard from './components/authenticated/guards/RoleGuard'
import AuthGuard from './components/authenticated/guards/AuthGuard'
import GuestGuard from './components/authenticated/guards/GuestGuard'
import BannerHost from './components/ui/Banner'
import { AppLayout } from './components/authenticated/layout/AccountLayout'

import { AppProvider } from './contexts/AppContext'
import { Banner } from './types/contexts/AppContext'
import { RoleType } from './utils/jwtUtils'
import { FeatureFlags, isFeatureEnabled } from './constants/featureFlags'

function App() {
  const [banner, setBanner] = useState<Banner | null>(null)
  const isFeatureFlagEnabled = isFeatureEnabled(FeatureFlags.FF_REGISTER)
  const partnersEnabled = isFeatureEnabled(FeatureFlags.FF_PARTNERS)
  const membershipsEnabled = isFeatureEnabled(FeatureFlags.FF_MEMBERSHIPS)

  const featureFlagRoutes = () => {
    return (
      <>
        <Route element={<GuestGuard />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* Reachable by guests and signed-in users; the payment provider redirects here. */}
        <Route element={<CheckoutLayout />}>
          <Route path="/checkout" element={<Checkout />} />
        </Route>

        <Route element={<GuestGuard />}>
          <Route element={<GuestLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/guest-clubs" element={<GuestClubs />} />
            <Route path="/guest-booking" element={<GuestBooking />} />
            <Route path="/guest-booking/checkout" element={<GuestCheckout />} />
            <Route path="/guest-booking/confirm" element={<Confirm />} />
            <Route
              path="/guest-memberships"
              element={
                membershipsEnabled ? (
                  <GuestMemberships />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        <Route element={<AuthGuard />}>
          <Route element={<AppLayout />}>
            <Route element={<ProfileCompletionGuard />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/history" element={<History />} />
              <Route path="/history/reschedule/:bookingId" element={<Reschedule />} />
              <Route
                path="/passes"
                element={
                  membershipsEnabled ? (
                    <Passes />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route path="/memberships" element={<Navigate to="/passes" replace />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/setup" element={<AccountSetup />} />
              <Route path="/account/:tab" element={<Account />} />
              <Route path="/account/terms-and-conditions" element={<Terms />} />
              <Route
                path="/partners"
                element={
                  partnersEnabled ? (
                    <Partners />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />
              <Route path="/confirm" element={<Confirm />} />
              <Route path="/help" element={<Help />} />

              <Route
                element={
                  <RoleGuard
                    allowedRoles={[RoleType.MANAGER, RoleType.ADMIN]}
                  />
                }
              >
                <Route path="/m/dashboard" element={<Navigate to="/m/all" replace />} />
                <Route path="/m/booking" element={<ManagerBooking />} />
                <Route path="/m/all" element={<ManageAllBookings />} />
                <Route path="/m/courts" element={<Courts />} />
                <Route path="/m/courts/new" element={<Court />} />
                <Route path="/m/courts/club/:clubId" element={<Courts />} />
                <Route path="/m/courts/:courtId" element={<Court />} />
                <Route path="/m/clubs" element={<ManagerClubs />} />
                <Route path="/m/clubs/new" element={<ManagerClub />} />
                <Route path="/m/clubs/:clubId" element={<ManagerClub />} />
                <Route path="/m/passes" element={<ManagerPasses />} />
                <Route path="/m/passes/new" element={<ManagerPass />} />
                <Route path="/m/passes/:passProductId" element={<ManagerPass />} />
                <Route path="/m/pricing" element={<ManagerPricing />} />
                <Route path="/m/pricing/new" element={<ManagerPriceRule />} />
                <Route path="/m/pricing/:priceRuleId" element={<ManagerPriceRule />} />
                <Route path="/m/blocks" element={<ManagerBlocks />} />
                <Route path="/m/payments" element={<Payments />} />
                <Route path="/m/blocks/new" element={<ManagerBlock />} />
                <Route
                  path="/m/blocks/:blockId/edit"
                  element={<ManagerBlock />}
                />
              </Route>

              <Route element={<RoleGuard allowedRoles={[RoleType.ADMIN]} />}>
                <Route path="/a/reports" element={<AdminReports />} />
                <Route path="/a/users" element={<AdminUsers />} />
                <Route path="/a/users/:profileId" element={<AdminUser />} />
                <Route path="/a/audit" element={<AdminAudit />} />
                <Route path="/a/account-actions" element={<Navigate to="/a/audit" replace />} />
                <Route path="/a/login-audit" element={<Navigate to="/a/audit?view=sign-ins" replace />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </>
    )
  }

  const preLaunchRoutes = () => {
    return (
      <>
        <Route element={<GuestLayout />}>
          <Route path="/" element={<StaticHome />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </>
    )
  }

  return (
    <AppProvider bannerState={[banner, setBanner]}>
      <Suspense fallback={<Loading />}>
        <Routes>
          {isFeatureFlagEnabled ? featureFlagRoutes() : preLaunchRoutes()}
        </Routes>
      </Suspense>
      <BannerHost banner={banner} onClose={() => setBanner(null)} />
    </AppProvider>
  )
}

export default App
